"use client";

import type { HeroStats, HeroTrustChip } from "@/components/landing/Hero/types";
import type { HeroCta } from "@/components/landing/types";
import HeroButtons from "@/components/landing/Hero/HeroButtons";
import HeroStatsRow from "@/components/landing/Hero/HeroStatsRow";
import HeroTrustChips from "@/components/landing/Hero/HeroTrustChips";
import { MotionReveal, useSectionMotion } from "@/components/landing/shared";

interface HeroContentProps {
	badge?: string;
	title: string;
	titleAccent?: string;
	subtitle: string;
	primary: HeroCta;
	secondary?: HeroCta | null;
	trustChips?: HeroTrustChip[];
	stats?: HeroStats | null;
}

function HeroContent(prop: HeroContentProps) {
	const { badge, title, titleAccent, subtitle, primary, secondary, trustChips, stats } = prop;
	const motion = useSectionMotion();
	const titleParts = titleAccent && title.includes(titleAccent) ? title.split(titleAccent) : null;

	return (
		<div className="max-w-3xl">
			{badge && (
				<MotionReveal motion={motion} eager>
					<span
						className="badge mb-6 border px-3 py-1 text-xs"
						style={{
							borderColor: "color-mix(in srgb, var(--brand-1) 35%, transparent)",
							background: "color-mix(in srgb, var(--brand-2) 12%, transparent)",
							color: "var(--brand-1)",
						}}
					>
						{badge}
					</span>
				</MotionReveal>
			)}

			<MotionReveal motion={motion} delayOffset={80} eager>
				<h1 className="text-balance text-5xl font-extrabold leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl">
					{titleParts ? (
						<>
							{titleParts[0]}
							<span style={{ color: "var(--brand-1)" }}>{titleAccent}</span>
							{titleParts[1]}
						</>
					) : (
						title
					)}
				</h1>
			</MotionReveal>

			<MotionReveal motion={motion} delayOffset={160} eager>
				<p className="mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
					{subtitle}
				</p>
			</MotionReveal>

			<MotionReveal motion={motion} delayOffset={240} eager>
				<HeroButtons primary={primary} secondary={secondary} />
			</MotionReveal>

			{trustChips && trustChips.length > 0 && (
				<MotionReveal motion={motion} delayOffset={300} eager>
					<HeroTrustChips chips={trustChips} />
				</MotionReveal>
			)}

			{stats && (
				<MotionReveal motion={motion} delayOffset={340} eager>
					<HeroStatsRow stats={stats} />
				</MotionReveal>
			)}
		</div>
	);
}

export default HeroContent;
